import React, { useState, useContext } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { openaiService, TradingStrategy } from '../services/openaiService';
import { MetamaskContext } from '../contexts/MetamaskContext';
import { WalletConnectContext } from '../contexts/WalletConnectContext';
import CopyCatAgent from './CopyCatAgent';
import TwitterNews from './TwitterNews';
import AIInsights from './AIInsights';

interface Message {
  id: number;
  content: string;
  sender: 'user' | 'ai';
  timestamp: string;
}

const ChatHub: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      content: "🤖 COPYCAT AI Strategy Generator Ready\n\nI'm your advanced AI trading assistant powered by OpenAI GPT-4. I specialize in creating custom trading strategies, analyzing market conditions, and optimizing your trading performance.\n\n⚡ Quick Actions:\n• Generate custom trading strategies\n• Analyze market trends and opportunities\n• Optimize existing strategies\n• Risk assessment and management\n\nWhat trading strategy would you like me to create for you?",
      sender: 'ai',
      timestamp: new Date().toLocaleTimeString()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'history' | 'news' | 'insights'>('chat');
  const [strategies, setStrategies] = useState<TradingStrategy[]>([]);
  const [newsToken, setNewsToken] = useState<string>('');

  const { metamaskAccountAddress } = useContext(MetamaskContext);
  const { accountId } = useContext(WalletConnectContext);
  
  const walletAddress = metamaskAccountAddress || accountId || '';
  const formatAddress = (address: string) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isProcessing) return;

    const newMessage: Message = {
      id: messages.length + 1,
      content: inputMessage,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString()
    };

    setMessages(prev => [...prev, newMessage]);
    const currentInput = inputMessage;
    setInputMessage('');
    setIsProcessing(true);

    try {
      // Get AI response from OpenAI
      const aiResponseContent = await openaiService.generateResponse(currentInput);
      
      const aiResponse: Message = {
        id: messages.length + 2,
        content: aiResponseContent,
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString()
      };
      
      setMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      const errorResponse: Message = {
        id: messages.length + 2,
        content: "I'm experiencing some technical difficulties. Please try again in a moment.",
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAgentResponse = (response: string) => {
    const aiResponse: Message = {
      id: messages.length + 1,
      content: response,
      sender: 'ai',
      timestamp: new Date().toLocaleTimeString()
    };
    setMessages(prev => [...prev, aiResponse]);
    setActiveTab('chat'); // Switch to chat tab to show the response
  };
  const handleStrategyGenerated = (strategy: TradingStrategy) => {
    console.log('New strategy generated:', strategy);
    setStrategies(prev => [...prev, strategy]);
  };

  const handleTokenNews = (token: string) => {
    setNewsToken(token);
    setActiveTab('news');
  };

  const quickActions = [
    { label: "Generate Strategy", icon: "🎯", action: () => setInputMessage("Generate a trading strategy for BTC with medium risk") },
    { label: "Market Analysis", icon: "📊", action: () => setInputMessage("Analyze current market conditions and opportunities") },
    { label: "Optimize Portfolio", icon: "⚡", action: () => setInputMessage("Help me optimize my trading portfolio") },
    { label: "Risk Management", icon: "🛡️", action: () => setInputMessage("Create a risk management strategy") },
    { label: "BTC News", icon: "📰", action: () => handleTokenNews("BTC") },
    { label: "ETH News", icon: "📰", action: () => handleTokenNews("ETH") },
    { label: "AVNT News", icon: "📈", action: () => handleTokenNews("AVNT") },
    { label: "Custom Token News", icon: "🔍", action: () => {
      const token = prompt("Enter token symbol (e.g., BTC, ETH, DOGE):");
      if (token) handleTokenNews(token.toUpperCase());
    }}
  ];

  return (
    <div className="h-full bg-black text-green-400 font-mono flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-green-500/30 bg-[#030306]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-green-400 flex items-center space-x-2">
              <span>🤖</span>
              <span>COPYCAT AI Strategy Generator</span>
            </h1>
            <div className="text-sm text-green-500/70 flex items-center space-x-4">
              <span>Connected: {formatAddress(walletAddress)}</span>
              <span>•</span>
              <span className="flex items-center space-x-1">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span>Powered by OpenAI GPT-4</span>
              </span>
            </div>
          </div>
          <div className="text-green-400/70 text-sm">
            {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex space-x-1">
          <Button
            onClick={() => setActiveTab('chat')}
            className={`px-4 py-2 text-sm rounded transition-colors ${
              activeTab === 'chat'
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-transparent text-green-400/70 hover:bg-green-500/10 hover:text-green-400'
            }`}
          >
            💬 Copy Chat
          </Button>
          <Button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 text-sm rounded transition-colors ${
              activeTab === 'history'
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-transparent text-green-400/70 hover:bg-green-500/10 hover:text-green-400'
            }`}
          >
            📚 History
          </Button>
          <Button
            onClick={() => setActiveTab('news')}
            className={`px-4 py-2 text-sm rounded transition-colors ${
              activeTab === 'news'
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-transparent text-green-400/70 hover:bg-green-500/10 hover:text-green-400'
            }`}
          >
            📰 News
          </Button>
          <Button
            onClick={() => setActiveTab('insights')}
            className={`px-4 py-2 text-sm rounded transition-colors ${
              activeTab === 'insights'
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-transparent text-green-400/70 hover:bg-green-500/10 hover:text-green-400'
            }`}
          >
            🧠 Insights
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'chat' ? (
          <div className="h-full flex flex-col">
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      message.sender === 'user'
                        ? 'bg-green-500/20 border border-green-500/30 text-green-300'
                        : 'bg-[#030306] border border-green-500/20 text-green-400'
                    }`}
                  >
                    <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                    <div className="text-xs text-green-500/50 mt-1">{message.timestamp}</div>
                  </div>
                </div>
              ))}
              
              {/* Processing Indicator */}
              {isProcessing && (
                <div className="flex justify-start">
                  <div className="bg-[#030306] border border-green-500/20 text-green-400 px-4 py-2 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm">COPYCAT is thinking...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'news' ? (
          <div className="h-full overflow-y-auto p-4">
            {/* Twitter News Tab */}
            {newsToken ? (
              <TwitterNews 
                token={newsToken} 
                onClose={() => {
                  setNewsToken('');
                  setActiveTab('chat');
                }}
              />
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📰</div>
                <h3 className="text-xl font-bold text-green-400 mb-2">Twitter News & Sentiment</h3>
                <p className="text-green-400/70 mb-6">Get live Twitter sentiment analysis for any token</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-md mx-auto">
                  {['BTC', 'ETH', 'SOL', 'AVNT'].map(token => (
                    <Button
                      key={token}
                      onClick={() => handleTokenNews(token)}
                      className="bg-green-600/30 border border-green-500/40 text-green-300 hover:bg-green-500/20"
                    >
                      ${token}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'insights' ? (
          <div className="h-full overflow-y-auto p-4">
            {/* AI Insights Tab */}
            <AIInsights 
              onTokenSelect={(symbol) => {
                setInputMessage(`Analyze ${symbol} and give me a trading strategy`);
                setActiveTab('chat');
              }}
            />
          </div>
        ) : (
          <div className="h-full overflow-y-auto p-4">
            {/* History Tab - Show strategies and chat history */}
            <div className="space-y-6">
              {/* Generated Strategies */}
              <div>
                <h3 className="text-lg font-bold text-green-400 mb-4">🤖 Generated Strategies</h3>
                {strategies.length === 0 ? (
                  <div className="text-center py-8 text-green-400/50">
                    <div className="text-4xl mb-2">📊</div>
                    <div>No strategies generated yet</div>
                    <div className="text-sm mt-1">Use the Copy Chat to generate your first strategy</div>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {strategies.map((strategy, index) => (
                      <Card key={index} className="p-4 bg-gradient-to-br from-[#0d0d2a] to-[#0a0a1a] border-green-500/30">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="text-green-300 font-bold text-lg">{strategy.name}</h4>
                            <div className="flex items-center space-x-4 text-sm text-green-400/70 mt-1">
                              <span className="bg-green-500/20 px-2 py-1 rounded-full">{strategy.type}</span>
                              <span className="bg-blue-500/20 px-2 py-1 rounded-full">{strategy.risk} Risk</span>
                              <span className="bg-purple-500/20 px-2 py-1 rounded-full">{strategy.timeframe}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-green-400 font-bold">{strategy.expectedReturn}</div>
                            <div className="text-green-400/70 text-sm">Expected Return</div>
                          </div>
                        </div>
                        <p className="text-green-400/80 text-sm mb-3">{strategy.description}</p>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="text-green-300 font-medium mb-2">Entry Signals:</h5>
                            <ul className="text-green-400/70 text-sm space-y-1">
                              {strategy.signals.map((signal, i) => (
                                <li key={i}>• {signal}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h5 className="text-green-300 font-medium mb-2">Risk Management:</h5>
                            <div className="text-green-400/70 text-sm space-y-1">
                              <div>• Stop Loss: {strategy.stopLoss}</div>
                              <div>• Take Profit: {strategy.takeProfit}</div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Chat History */}
              <div>
                <h3 className="text-lg font-bold text-green-400 mb-4">💬 Chat History</h3>
                <div className="space-y-3">
                  {messages.slice(1).map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          message.sender === 'user'
                            ? 'bg-green-500/20 border border-green-500/30 text-green-300'
                            : 'bg-[#030306] border border-green-500/20 text-green-400'
                        }`}
                      >
                        <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                        <div className="text-xs text-green-500/50 mt-1">{message.timestamp}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Chat Input - Only show in chat mode */}
      {activeTab === 'chat' && (
        <div className="p-4 border-t border-green-500/30 bg-[#030306]">
          <div className="mb-4">
            <div className="text-sm text-green-400/70 mb-2">Quick Actions:</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {quickActions.map((action, index) => (
                <Button
                  key={index}
                  onClick={action.action}
                  variant="outline"
                  size="sm"
                  className="bg-transparent border-green-500/30 text-green-400/70 hover:bg-green-500/10 hover:text-green-300 text-xs"
                  disabled={isProcessing}
                >
                  <span className="mr-1">{action.icon}</span>
                  {action.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Message Input */}
          <div className="flex items-center space-x-2">
            <div className="text-green-400 text-sm">$</div>
            <input
              type="text"
              value={inputMessage}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputMessage(e.target.value)}
              placeholder="Describe the trading strategy you want me to create..."
              className="flex-1 bg-transparent border border-green-500/30 rounded px-3 py-2 text-green-400 placeholder-green-400/50 focus:border-green-400 focus:outline-none"
              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && handleSendMessage()}
              disabled={isProcessing}
            />
            <Button
              onClick={handleSendMessage}
              className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 px-4"
              disabled={isProcessing || !inputMessage.trim()}
            >
              {isProcessing ? '⏳' : '➤'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatHub;
